// Binary wire format between the browser (src/client/net.ts) and the Arena Durable Object (src/worker/arena.ts).
// Layouts are in docs/ARCHITECTURE.md. Little-endian; the first byte is the message type.
// Positions u16 = x * 16, radii u16 = r * 8, timers u16 = seconds * 10, pellet radius u8 = r * 2.
// Encoders size the buffer up front and write it once. Decoders bounds-check every read and return null on
// malformed input instead of throwing: whatever arrives over a socket is hostile until proven otherwise.
import type { PelletKind, SimEvent } from './types';
import { ALL_SKINS, EVENT_TYPES, PELLET_COLORS, POWERUP_TYPES, RUSH_COLOR, SKIN_BY_ID, WORLD } from './config';
import type { EventType, PowerUpType, SkinId } from './config';

export const MSG = {
  // client -> server
  INPUT: 0x01, RESPAWN: 0x02, PING: 0x03, VIEW: 0x04,
  // server -> client
  WELCOME: 0x10, ROSTER: 0x11, SNAPSHOT: 0x12, PELLETS: 0x13, EVENTS: 0x14, BOARD: 0x15, PONG: 0x16, RADAR: 0x17,
} as const;

// ========== quantization ==========
export const POS_SCALE = 16, RAD_SCALE = 8, TIME_SCALE = 10, PELLET_R_SCALE = 2;
export const MAX_DELTA = WORLD; // INPUT dx/dy are clamped to +-WORLD
export const NO_EVENT = 255, NO_RANK = 255;
// rounds, then clamps into [lo, hi]; NaN becomes lo
const qInt = (v: number, lo: number, hi: number) => { const r = Math.round(v); return r >= lo ? (r <= hi ? r : hi) : lo; };
export const qPos = (x: number) => qInt(x * POS_SCALE, 0, 0xffff);
export const dqPos = (q: number) => q / POS_SCALE;
export const qRad = (r: number) => qInt(r * RAD_SCALE, 0, 0xffff);
export const dqRad = (q: number) => q / RAD_SCALE;
export const qTime = (sec: number) => qInt(sec * TIME_SCALE, 0, 0xffff);
export const dqTime = (q: number) => q / TIME_SCALE;
export const qPelletR = (r: number) => qInt(r * PELLET_R_SCALE, 0, 0xff);
export const dqPelletR = (q: number) => q / PELLET_R_SCALE;
export const qDelta = (d: number) => (Number.isNaN(d) ? 0 : qInt(d, -MAX_DELTA, MAX_DELTA));

// ========== pellet colours ==========
// colour byte: 0-7 PELLET_COLORS, 8 rush gold, 16+i = ALL_SKINS[i] colour (ejected mass and boost trail dots)
export const RUSH_COLOR_INDEX = 8, SKIN_COLOR_BASE = 16;
const COLOR_BY_INDEX: (string | undefined)[] = [];
PELLET_COLORS.forEach((c, i) => { COLOR_BY_INDEX[i] = c; });
COLOR_BY_INDEX[RUSH_COLOR_INDEX] = RUSH_COLOR;
ALL_SKINS.forEach((s, i) => { COLOR_BY_INDEX[SKIN_COLOR_BASE + i] = s.color; });
const FOOD_INDEX = new Map(PELLET_COLORS.map((c, i) => [c.toLowerCase(), i]));
const SKIN_COLOR_INDEX = new Map(ALL_SKINS.map((s, i) => [s.color.toLowerCase(), SKIN_COLOR_BASE + i]));
export function pelletColorIndex(color: string, kind: PelletKind): number {
  const c = color.toLowerCase();
  if (kind === 2) return RUSH_COLOR_INDEX;
  if (kind === 0) return FOOD_INDEX.get(c) ?? SKIN_COLOR_INDEX.get(c) ?? 0;
  return SKIN_COLOR_INDEX.get(c) ?? FOOD_INDEX.get(c) ?? (c === RUSH_COLOR.toLowerCase() ? RUSH_COLOR_INDEX : 0);
}
export const pelletColor = (index: number): string | undefined => COLOR_BY_INDEX[index];

// ========== message shapes (decoded values are in world units / seconds) ==========
export interface InputMsg {
  dx: number; dy: number; split: boolean; eject: boolean; boost: boolean;
  aim?: boolean; // dx/dy only aim split/feed, the blob stands still (idle touch joystick); omitted when false
}
export interface RosterEntry { id: number; name: string; skin: SkinId; bot: boolean }
export interface NetCell { id: number; owner: number; x: number; y: number; r: number; flags: number }
export interface NetVirus { id: number; x: number; y: number; r: number }
export interface NetPowerUp { id: number; type: PowerUpType; x: number; y: number; ageMs: number }
export interface NetFx { type: PowerUpType; remaining: number }
export interface NetEvent { type: EventType; remaining: number; dur: number }
export interface SnapshotMsg {
  serverMs: number; myAlive: boolean; myMass: number;
  cells: NetCell[]; viruses: NetVirus[]; powerups: NetPowerUp[]; fx: NetFx[]; event: NetEvent | null;
}
export interface PelletAdd { id: number; x: number; y: number; r: number; color: number; kind: PelletKind }
export interface PelletMove { id: number; x: number; y: number }
export interface PelletsMsg { adds: PelletAdd[]; removes: number[]; moves: PelletMove[] }
export interface BoardRow { name: string; mass: number; me: boolean }
export interface BoardMsg { myRank: number; rows: BoardRow[] } // myRank 0-based, -1 = not on the board
export interface ViewMsg { w: number; h: number } // the client's viewport in CSS px (only its shape matters to the Arena)
export interface RadarDot { x: number; y: number; r: number; owner: number } // a cell outside my snapshot view

export type ClientMsg =
  | { t: 'input'; input: InputMsg } | { t: 'respawn' } | { t: 'ping'; time: number } | { t: 'view'; view: ViewMsg };
export type ServerMsg =
  | { t: 'welcome'; myId: number; room: string; resumed: boolean }
  | { t: 'roster'; roster: RosterEntry[] }
  | { t: 'snapshot'; snap: SnapshotMsg }
  | { t: 'pellets'; delta: PelletsMsg }
  | { t: 'events'; events: SimEvent[] }
  | { t: 'board'; board: BoardMsg }
  | { t: 'pong'; time: number }
  | { t: 'radar'; dots: RadarDot[] };

// ========== byte helpers ==========
const utf8 = new TextEncoder();
const strictUtf8 = new TextDecoder('utf-8', { fatal: true });
// utf-8 bytes of s, cut at a character boundary so they fit in max bytes
function utf8Bytes(s: string, max: number): Uint8Array {
  const b = utf8.encode(s);
  if (b.length <= max) return b;
  let n = max;
  while (n > 0 && (b[n] & 0xc0) === 0x80) n--;
  return b.subarray(0, n);
}
class Writer {
  readonly buf: ArrayBuffer;
  private v: DataView;
  private o = 0;
  constructor(size: number) { this.buf = new ArrayBuffer(size); this.v = new DataView(this.buf); }
  u8(x: number) { this.v.setUint8(this.o, x); this.o += 1; }
  u16(x: number) { this.v.setUint16(this.o, x, true); this.o += 2; }
  i16(x: number) { this.v.setInt16(this.o, x, true); this.o += 2; }
  u32(x: number) { this.v.setUint32(this.o, x >>> 0, true); this.o += 4; }
  f32(x: number) { this.v.setFloat32(this.o, x, true); this.o += 4; }
  bytes(b: Uint8Array) { new Uint8Array(this.buf, this.o, b.length).set(b); this.o += b.length; }
}
// Out-of-range reads return 0 and set bad instead of throwing; decoders check bad (and left) before trusting values.
class Reader {
  bad = false;
  private o = 0;
  constructor(private v: DataView) {}
  get left() { return this.v.byteLength - this.o; }
  private take(n: number): number {
    if (this.o + n > this.v.byteLength) { this.bad = true; return -1; }
    const at = this.o; this.o += n; return at;
  }
  u8() { const at = this.take(1); return at < 0 ? 0 : this.v.getUint8(at); }
  u16() { const at = this.take(2); return at < 0 ? 0 : this.v.getUint16(at, true); }
  i16() { const at = this.take(2); return at < 0 ? 0 : this.v.getInt16(at, true); }
  u32() { const at = this.take(4); return at < 0 ? 0 : this.v.getUint32(at, true); }
  f32() { const at = this.take(4); return at < 0 ? 0 : this.v.getFloat32(at, true); }
  bytes(n: number): Uint8Array | null {
    const at = this.take(n);
    return at < 0 ? null : new Uint8Array(this.v.buffer, this.v.byteOffset + at, n);
  }
  // all bytes read, nothing left over
  get done() { return !this.bad && this.left === 0; }
}
function viewOf(data: unknown): DataView | null {
  if (data instanceof ArrayBuffer) return new DataView(data);
  if (ArrayBuffer.isView(data)) return new DataView(data.buffer, data.byteOffset, data.byteLength);
  return null;
}
// reader positioned after the type byte, or null if the buffer is not a message of that type
function open(data: unknown, type: number): Reader | null {
  const v = viewOf(data);
  if (!v || v.byteLength < 1 || v.getUint8(0) !== type) return null;
  const r = new Reader(v);
  r.u8();
  return r;
}
function utf8Text(r: Reader, n: number): string | null {
  const b = r.bytes(n);
  if (!b) return null;
  try { return strictUtf8.decode(b); } catch { return null; }
}
function jsonBody(r: Reader): unknown {
  const s = utf8Text(r, r.left);
  if (s === null) return undefined;
  try { return JSON.parse(s); } catch { return undefined; }
}
function jsonMessage(type: number, value: unknown): ArrayBuffer {
  const b = utf8.encode(JSON.stringify(value));
  const w = new Writer(1 + b.length);
  w.u8(type); w.bytes(b);
  return w.buf;
}
const isInt = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
// own keys only, so '__proto__', 'toString', 'constructor'... are never skins (same rule as config's isSkinId)
export const isKnownSkin = (v: unknown): v is SkinId => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SKIN_BY_ID, v);

// ========== client -> server ==========
// INPUT {dx i16, dy i16, buttons u8}: bit0 split pressed since last INPUT, bit1 eject held, bit2 boost held,
// bit3 aim only (dx/dy is where split/feed fire; the blob stands still)
export const INPUT_BYTES = 6;
export function encodeInput(i: InputMsg): ArrayBuffer {
  const w = new Writer(INPUT_BYTES);
  w.u8(MSG.INPUT); w.i16(qDelta(i.dx)); w.i16(qDelta(i.dy));
  w.u8((i.split ? 1 : 0) | (i.eject ? 2 : 0) | (i.boost ? 4 : 0) | (i.aim ? 8 : 0));
  return w.buf;
}
export function decodeInput(data: unknown): InputMsg | null {
  const r = open(data, MSG.INPUT);
  if (!r) return null;
  const dx = r.i16(), dy = r.i16(), b = r.u8();
  if (!r.done || Math.abs(dx) > MAX_DELTA || Math.abs(dy) > MAX_DELTA || b & ~15) return null;
  const out: InputMsg = { dx, dy, split: !!(b & 1), eject: !!(b & 2), boost: !!(b & 4) };
  if (b & 8) out.aim = true;
  return out;
}
export function encodeRespawn(): ArrayBuffer {
  const w = new Writer(1);
  w.u8(MSG.RESPAWN);
  return w.buf;
}
export function decodeRespawn(data: unknown): true | null {
  const r = open(data, MSG.RESPAWN);
  return r && r.done ? true : null;
}
function encodeTime(type: number, t: number): ArrayBuffer {
  const w = new Writer(5);
  w.u8(type); w.u32(Math.floor(t));
  return w.buf;
}
function decodeTime(data: unknown, type: number): number | null {
  const r = open(data, type);
  if (!r) return null;
  const t = r.u32();
  return r.done ? t : null;
}
// PING/PONG {t u32}: the client's clock in ms (mod 2^32), echoed back
export const encodePing = (t: number) => encodeTime(MSG.PING, t);
export const decodePing = (data: unknown) => decodeTime(data, MSG.PING);
export const encodePong = (t: number) => encodeTime(MSG.PONG, t);
export const decodePong = (data: unknown) => decodeTime(data, MSG.PONG);
// VIEW {w u16, h u16}: viewport size in CSS px, sent after the socket opens and again when it changes.
// Both sides must be at least 1; the Arena only uses the shape (clamped to 1:1 .. 3.5:1), never the size.
export const VIEW_BYTES = 5;
export function encodeView(v: ViewMsg): ArrayBuffer {
  const w = new Writer(VIEW_BYTES);
  w.u8(MSG.VIEW); w.u16(qInt(v.w, 1, 0xffff)); w.u16(qInt(v.h, 1, 0xffff));
  return w.buf;
}
export function decodeView(data: unknown): ViewMsg | null {
  const r = open(data, MSG.VIEW);
  if (!r) return null;
  const w = r.u16(), h = r.u16();
  return r.done && w > 0 && h > 0 ? { w, h } : null;
}

// ========== server -> client ==========
// WELCOME {myId u16, roomLen u8, room utf8, resumed u8}
// resumed = 1: this socket took over a seat the Arena still held (a reconnect inside the grace, or another tab),
// so myId is the same blob as before. 0: a new seat (and a new blob) — after a reconnect that means the old one
// is gone (the grace ran out, or the Arena restarted) and the client must not carry on as if nothing happened.
export function encodeWelcome(myId: number, room: string, resumed: boolean): ArrayBuffer {
  const b = utf8Bytes(room, 255);
  const w = new Writer(5 + b.length);
  w.u8(MSG.WELCOME); w.u16(myId & 0xffff); w.u8(b.length); w.bytes(b); w.u8(resumed ? 1 : 0);
  return w.buf;
}
export function decodeWelcome(data: unknown): { myId: number; room: string; resumed: boolean } | null {
  const r = open(data, MSG.WELCOME);
  if (!r) return null;
  const myId = r.u16(), n = r.u8();
  if (r.bad) return null;
  const room = utf8Text(r, n);
  if (room === null) return null;
  const resumed = r.u8();
  return r.done && resumed <= 1 ? { myId, room, resumed: resumed === 1 } : null;
}

// ROSTER {json}: [{id, name, skin, bot}]
export const MAX_ROSTER = 256, MAX_NAME_CHARS = 64;
export function encodeRoster(entries: RosterEntry[]): ArrayBuffer {
  return jsonMessage(MSG.ROSTER, entries.map(e => ({ id: e.id & 0xffff, name: e.name, skin: e.skin, bot: e.bot })));
}
export function decodeRoster(data: unknown): RosterEntry[] | null {
  const r = open(data, MSG.ROSTER);
  if (!r) return null;
  const v = jsonBody(r);
  if (!Array.isArray(v) || v.length > MAX_ROSTER) return null;
  const out: RosterEntry[] = [];
  for (const e of v) {
    if (!isObj(e) || !isInt(e.id, 0, 0xffff) || typeof e.name !== 'string' || e.name.length > MAX_NAME_CHARS ||
      !isKnownSkin(e.skin) || typeof e.bot !== 'boolean') return null;
    out.push({ id: e.id, name: e.name, skin: e.skin, bot: e.bot });
  }
  return out;
}

// SNAPSHOT {serverMs u32, myAlive u8, myMass f32, cells u16 x [id u16, owner u16, x u16, y u16, r u16, flags u8],
//   viruses u8 x [id u16, x u16, y u16, r u16], powerups u8 x [id u16, type u8, x u16, y u16, ageMs u16],
//   fx u8 x [type u8, remaining u16], event [type u8 (255 none), remaining u16, dur u16]}
const CELL_BYTES = 11, VIRUS_BYTES = 8, POWERUP_BYTES = 9, FX_BYTES = 3, EVENT_BYTES = 5;
const SNAP_HEAD = 1 + 4 + 1 + 4;
export function snapshotSize(s: SnapshotMsg): number {
  return SNAP_HEAD + 2 + Math.min(s.cells.length, 0xffff) * CELL_BYTES + 1 + Math.min(s.viruses.length, 255) * VIRUS_BYTES +
    1 + Math.min(s.powerups.length, 255) * POWERUP_BYTES + 1 + Math.min(s.fx.length, 255) * FX_BYTES + EVENT_BYTES;
}
export function encodeSnapshot(s: SnapshotMsg): ArrayBuffer {
  const w = new Writer(snapshotSize(s));
  w.u8(MSG.SNAPSHOT); w.u32(s.serverMs); w.u8(s.myAlive ? 1 : 0); w.f32(Number.isFinite(s.myMass) ? Math.max(0, s.myMass) : 0);
  const nc = Math.min(s.cells.length, 0xffff);
  w.u16(nc);
  for (let i = 0; i < nc; i++) {
    const c = s.cells[i];
    w.u16(c.id & 0xffff); w.u16(c.owner & 0xffff); w.u16(qPos(c.x)); w.u16(qPos(c.y)); w.u16(qRad(c.r)); w.u8(c.flags & 0xff);
  }
  const nv = Math.min(s.viruses.length, 255);
  w.u8(nv);
  for (let i = 0; i < nv; i++) {
    const v = s.viruses[i];
    w.u16(v.id & 0xffff); w.u16(qPos(v.x)); w.u16(qPos(v.y)); w.u16(qRad(v.r));
  }
  const np = Math.min(s.powerups.length, 255);
  w.u8(np);
  for (let i = 0; i < np; i++) {
    const p = s.powerups[i];
    w.u16(p.id & 0xffff); w.u8(Math.max(0, POWERUP_TYPES.indexOf(p.type))); w.u16(qPos(p.x)); w.u16(qPos(p.y));
    w.u16(qInt(p.ageMs, 0, 0xffff));
  }
  const nf = Math.min(s.fx.length, 255);
  w.u8(nf);
  for (let i = 0; i < nf; i++) {
    const f = s.fx[i];
    w.u8(Math.max(0, POWERUP_TYPES.indexOf(f.type))); w.u16(qTime(f.remaining));
  }
  const e = s.event, et = e ? EVENT_TYPES.indexOf(e.type) : -1;
  w.u8(e && et >= 0 ? et : NO_EVENT); w.u16(e ? qTime(e.remaining) : 0); w.u16(e ? qTime(e.dur) : 0);
  return w.buf;
}
export function decodeSnapshot(data: unknown): SnapshotMsg | null {
  const r = open(data, MSG.SNAPSHOT);
  if (!r) return null;
  const serverMs = r.u32(), alive = r.u8(), myMass = r.f32();
  if (r.bad || alive > 1 || !Number.isFinite(myMass) || myMass < 0) return null;
  const nc = r.u16();
  if (r.bad || nc * CELL_BYTES > r.left) return null;
  const cells: NetCell[] = new Array(nc);
  for (let i = 0; i < nc; i++) {
    cells[i] = { id: r.u16(), owner: r.u16(), x: dqPos(r.u16()), y: dqPos(r.u16()), r: dqRad(r.u16()), flags: r.u8() };
  }
  const nv = r.u8();
  if (r.bad || nv * VIRUS_BYTES > r.left) return null;
  const viruses: NetVirus[] = new Array(nv);
  for (let i = 0; i < nv; i++) viruses[i] = { id: r.u16(), x: dqPos(r.u16()), y: dqPos(r.u16()), r: dqRad(r.u16()) };
  const np = r.u8();
  if (r.bad || np * POWERUP_BYTES > r.left) return null;
  const powerups: NetPowerUp[] = new Array(np);
  for (let i = 0; i < np; i++) {
    const id = r.u16(), type = POWERUP_TYPES[r.u8()];
    if (!type) return null;
    powerups[i] = { id, type, x: dqPos(r.u16()), y: dqPos(r.u16()), ageMs: r.u16() };
  }
  const nf = r.u8();
  if (r.bad || nf * FX_BYTES > r.left) return null;
  const fx: NetFx[] = new Array(nf);
  for (let i = 0; i < nf; i++) {
    const type = POWERUP_TYPES[r.u8()];
    if (!type) return null;
    fx[i] = { type, remaining: dqTime(r.u16()) };
  }
  const et = r.u8(), rem = r.u16(), dur = r.u16();
  if (!r.done) return null;
  let event: NetEvent | null = null;
  if (et !== NO_EVENT) {
    const type = EVENT_TYPES[et];
    if (!type) return null;
    event = { type, remaining: dqTime(rem), dur: dqTime(dur) };
  }
  return { serverMs, myAlive: alive === 1, myMass, cells, viruses, powerups, fx, event };
}

// PELLETS delta {adds u16 x [id u16, x u16, y u16, r u8 (x2), color u8, kind u8], removes u16 x [id u16],
//   moves u16 x [id u16, x u16, y u16]}. Apply removes first, then adds, then moves (a freed id may come back as an add).
const ADD_BYTES = 9, MOVE_BYTES = 6;
export function encodePellets(d: PelletsMsg): ArrayBuffer {
  const na = Math.min(d.adds.length, 0xffff), nr = Math.min(d.removes.length, 0xffff), nm = Math.min(d.moves.length, 0xffff);
  const w = new Writer(1 + 2 + na * ADD_BYTES + 2 + nr * 2 + 2 + nm * MOVE_BYTES);
  w.u8(MSG.PELLETS);
  w.u16(na);
  for (let i = 0; i < na; i++) {
    const a = d.adds[i];
    w.u16(a.id & 0xffff); w.u16(qPos(a.x)); w.u16(qPos(a.y)); w.u8(qPelletR(a.r));
    w.u8(pelletColor(a.color) !== undefined ? a.color : 0); w.u8(a.kind & 3);
  }
  w.u16(nr);
  for (let i = 0; i < nr; i++) w.u16(d.removes[i] & 0xffff);
  w.u16(nm);
  for (let i = 0; i < nm; i++) {
    const m = d.moves[i];
    w.u16(m.id & 0xffff); w.u16(qPos(m.x)); w.u16(qPos(m.y));
  }
  return w.buf;
}
export function decodePellets(data: unknown): PelletsMsg | null {
  const r = open(data, MSG.PELLETS);
  if (!r) return null;
  const na = r.u16();
  if (r.bad || na * ADD_BYTES > r.left) return null;
  const adds: PelletAdd[] = new Array(na);
  for (let i = 0; i < na; i++) {
    const id = r.u16(), x = dqPos(r.u16()), y = dqPos(r.u16()), rr = dqPelletR(r.u8()), color = r.u8(), kind = r.u8();
    if (pelletColor(color) === undefined || kind > 3) return null;
    adds[i] = { id, x, y, r: rr, color, kind: kind as PelletKind };
  }
  const nr = r.u16();
  if (r.bad || nr * 2 > r.left) return null;
  const removes: number[] = new Array(nr);
  for (let i = 0; i < nr; i++) removes[i] = r.u16();
  const nm = r.u16();
  if (r.bad || nm * MOVE_BYTES > r.left) return null;
  const moves: PelletMove[] = new Array(nm);
  for (let i = 0; i < nm; i++) moves[i] = { id: r.u16(), x: dqPos(r.u16()), y: dqPos(r.u16()) };
  return r.done ? { adds, removes, moves } : null;
}

// EVENTS {json}: SimEvent[]. Each event is rebuilt from its known fields only, after type-checking them.
export const MAX_EVENTS = 512;
type FieldKind = 'n' | 's' | 'b' | 'pu' | 'ev';
const EVENT_FIELDS: Record<SimEvent['t'], [string, FieldKind][]> = {
  eat: [['player', 'n'], ['mass', 'n']],
  kill: [['killer', 'n'], ['victim', 'n'], ['victimName', 's'], ['combo', 'n'], ['golden', 'b']],
  death: [['player', 'n'], ['killerName', 's'], ['bestRank', 'n'], ['peakMass', 'n'], ['kills', 'n'], ['aliveSec', 'n']],
  split: [['player', 'n']],
  eject: [['player', 'n']],
  virusPop: [['player', 'n']],
  pickup: [['player', 'n'], ['type', 'pu'], ['x', 'n'], ['y', 'n']],
  eventStart: [['type', 'ev']],
  eventEnd: [['type', 'ev']],
  unlock: [['player', 'n']],
  first: [['player', 'n']],
  milestone: [['player', 'n'], ['mass', 'n']],
};
const EVENT_OPTIONAL: Partial<Record<SimEvent['t'], [string, FieldKind][]>> = {
  death: [['goldenKills', 'n'], ['points', 'n']],
  eventEnd: [['goldenEscaped', 'b']],
};
function fieldOk(v: unknown, k: FieldKind): boolean {
  switch (k) {
    case 'n': return isNum(v);
    case 's': return typeof v === 'string' && v.length <= MAX_NAME_CHARS;
    case 'b': return typeof v === 'boolean';
    case 'pu': return typeof v === 'string' && (POWERUP_TYPES as string[]).includes(v);
    case 'ev': return typeof v === 'string' && (EVENT_TYPES as string[]).includes(v);
  }
}
export function encodeEvents(events: SimEvent[]): ArrayBuffer {
  return jsonMessage(MSG.EVENTS, events.length > MAX_EVENTS ? events.slice(-MAX_EVENTS) : events);
}
export function decodeEvents(data: unknown): SimEvent[] | null {
  const r = open(data, MSG.EVENTS);
  if (!r) return null;
  const v = jsonBody(r);
  if (!Array.isArray(v) || v.length > MAX_EVENTS) return null;
  const out: SimEvent[] = [];
  for (const e of v) {
    if (!isObj(e) || typeof e.t !== 'string' || !Object.prototype.hasOwnProperty.call(EVENT_FIELDS, e.t)) return null;
    const t = e.t as SimEvent['t'];
    const ev: Record<string, unknown> = { t };
    for (const [f, k] of EVENT_FIELDS[t]) {
      if (!fieldOk(e[f], k)) return null;
      ev[f] = e[f];
    }
    for (const [f, k] of EVENT_OPTIONAL[t] ?? []) {
      if (e[f] === undefined) continue;
      if (!fieldOk(e[f], k)) return null;
      ev[f] = e[f];
    }
    out.push(ev as SimEvent);
  }
  return out;
}

// BOARD {myRank u8 (255 none), n u8 x [nameLen u8, name utf8, mass u32, me u8]}: ranked rows, biggest first
export function encodeBoard(b: BoardMsg): ArrayBuffer {
  const n = Math.min(b.rows.length, 255);
  const names: Uint8Array[] = new Array(n);
  let size = 3;
  for (let i = 0; i < n; i++) { names[i] = utf8Bytes(b.rows[i].name, 255); size += 1 + names[i].length + 4 + 1; }
  const w = new Writer(size);
  w.u8(MSG.BOARD); w.u8(b.myRank >= 0 && b.myRank < NO_RANK ? b.myRank : NO_RANK); w.u8(n);
  for (let i = 0; i < n; i++) {
    const row = b.rows[i];
    w.u8(names[i].length); w.bytes(names[i]); w.u32(qInt(Math.floor(row.mass), 0, 0xffffffff)); w.u8(row.me ? 1 : 0);
  }
  return w.buf;
}
export function decodeBoard(data: unknown): BoardMsg | null {
  const r = open(data, MSG.BOARD);
  if (!r) return null;
  const rank = r.u8(), n = r.u8();
  if (r.bad || n * 6 > r.left) return null;
  const rows: BoardRow[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const len = r.u8();
    if (r.bad) return null;
    const name = utf8Text(r, len);
    if (name === null) return null;
    const mass = r.u32(), me = r.u8();
    if (r.bad || me > 1) return null;
    rows[i] = { name, mass, me: me === 1 };
  }
  return r.done ? { myRank: rank === NO_RANK ? -1 : rank, rows } : null;
}

// RADAR {n u16 x [x u16, y u16, r u16, owner u16]}: other players' cells near me but outside my SNAPSHOT
// view, at 2 Hz, so the HUD radar shows as much online as it does solo
export const RADAR_DOT_BYTES = 8, MAX_RADAR_DOTS = 0xffff;
export function encodeRadar(dots: RadarDot[]): ArrayBuffer {
  const n = Math.min(dots.length, MAX_RADAR_DOTS);
  const w = new Writer(3 + n * RADAR_DOT_BYTES);
  w.u8(MSG.RADAR); w.u16(n);
  for (let i = 0; i < n; i++) {
    const d = dots[i];
    w.u16(qPos(d.x)); w.u16(qPos(d.y)); w.u16(qRad(d.r)); w.u16(d.owner & 0xffff);
  }
  return w.buf;
}
export function decodeRadar(data: unknown): RadarDot[] | null {
  const r = open(data, MSG.RADAR);
  if (!r) return null;
  const n = r.u16();
  if (r.bad || n * RADAR_DOT_BYTES !== r.left) return null;
  const dots: RadarDot[] = new Array(n);
  for (let i = 0; i < n; i++) dots[i] = { x: dqPos(r.u16()), y: dqPos(r.u16()), r: dqRad(r.u16()), owner: r.u16() };
  return r.done ? dots : null;
}

// ========== dispatch ==========
// what the Arena accepts from a socket; null for anything else (wrong type, wrong length, bad values)
export function decodeClient(data: unknown): ClientMsg | null {
  try {
    const v = viewOf(data);
    if (!v || v.byteLength < 1) return null;
    switch (v.getUint8(0)) {
      case MSG.INPUT: { const input = decodeInput(v); return input ? { t: 'input', input } : null; }
      case MSG.RESPAWN: return decodeRespawn(v) ? { t: 'respawn' } : null;
      case MSG.PING: { const time = decodePing(v); return time === null ? null : { t: 'ping', time }; }
      case MSG.VIEW: { const view = decodeView(v); return view ? { t: 'view', view } : null; }
      default: return null;
    }
  } catch { return null; }
}
export function decodeServer(data: unknown): ServerMsg | null {
  try {
    const v = viewOf(data);
    if (!v || v.byteLength < 1) return null;
    switch (v.getUint8(0)) {
      case MSG.WELCOME: { const w = decodeWelcome(v); return w ? { t: 'welcome', ...w } : null; }
      case MSG.ROSTER: { const roster = decodeRoster(v); return roster ? { t: 'roster', roster } : null; }
      case MSG.SNAPSHOT: { const snap = decodeSnapshot(v); return snap ? { t: 'snapshot', snap } : null; }
      case MSG.PELLETS: { const delta = decodePellets(v); return delta ? { t: 'pellets', delta } : null; }
      case MSG.EVENTS: { const events = decodeEvents(v); return events ? { t: 'events', events } : null; }
      case MSG.BOARD: { const board = decodeBoard(v); return board ? { t: 'board', board } : null; }
      case MSG.PONG: { const time = decodePong(v); return time === null ? null : { t: 'pong', time }; }
      case MSG.RADAR: { const dots = decodeRadar(v); return dots ? { t: 'radar', dots } : null; }
      default: return null;
    }
  } catch { return null; }
}
